import React, { useEffect, useRef, useState } from 'react'
import { Send, Archive, MessageCircle, Clock, CheckCircle, Globe, Mail, User, ChevronRight, ArrowLeft, Bot, UserRound, Sparkles, FolderArchive, ChevronLeft, Copy, Trash2, Check, FileText } from 'lucide-react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-50 text-green-600',
  waiting: 'bg-yellow-50 text-yellow-600',
  archived: 'bg-gray-100 text-gray-400',
  staff_active: 'bg-blue-50 text-blue-600',
}

export default function SupportPage() {
  const { user } = useAuth()
  const [conversations, setConversations] = useState<any[]>([])
  const [selected, setSelected] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [improvedReply, setImprovedReply] = useState('')
  const [improvingReply, setImprovingReply] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [unread, setUnread] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const [activeTab, setActiveTab] = useState<'client' | 'leads' | 'inquiries'>('client')
  const [inquiries, setInquiries] = useState<any[]>([])
  const [inquiriesLoading, setInquiriesLoading] = useState(false)
  const [selectedInquiry, setSelectedInquiry] = useState<any>(null)
  const [inquiryStats, setInquiryStats] = useState<any>(null)
  const [colorVisitStats, setColorVisitStats] = useState<any>(null)
  const [inquiryFilter, setInquiryFilter] = useState<'all' | 'submitted' | 'abandoned'>('all')
  const [inquiryPage, setInquiryPage] = useState(1)
  const [inquiryTotalPages, setInquiryTotalPages] = useState(1)
  const [leads, setLeads] = useState<any[]>([])
  const [leadsLoading, setLeadsLoading] = useState(false)
  const [leadsTotal, setLeadsTotal] = useState(0)
  const [leadsPage, setLeadsPage] = useState(1)
  const [selectedLead, setSelectedLead] = useState<any>(null)
  const [selectedLeadDetail, setSelectedLeadDetail] = useState<any>(null)
  const [copiedEmail, setCopiedEmail] = useState(false)

  const fetchConversations = async () => {
    try {
      const res = await api.get(`/support/conversations?archived=${showArchived}`)
      setConversations(res.data)
      const totalUnread = res.data.reduce((sum: number, c: any) => sum + (c.status === 'waiting' ? 1 : 0), 0)
      setUnread(totalUnread)
    } catch {}
    setLoading(false)
  }

  const loadConversation = async (id: string) => {
    try {
      const res = await api.get(`/support/conversations/${id}`)
      setSelected(res.data)
      setTimeout(() => {
        const container = messagesEndRef.current?.parentElement
        if (!container) return
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150
        if (isNearBottom) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch {}
  }

  useEffect(() => {
    fetchConversations()
    pollRef.current = setInterval(fetchConversations, 10000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [showArchived])

  useEffect(() => {
    if (selected) {
      const interval = setInterval(() => loadConversation(selected.id), 8000)
      return () => clearInterval(interval)
    }
  }, [selected?.id])

  const handleReply = async () => {
    if (!reply.trim() || !selected || sending) return
    setSending(true)
    try {
      const res = await api.post(`/support/conversations/${selected.id}/reply`, { content: reply })
      setSelected((prev: any) => prev ? {
        ...prev,
        messages: [...prev.messages, { ...res.data, senderName: user?.name }]
      } : null)
      setReply('')
      fetchConversations()
    } catch {}
    setSending(false)
  }

  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive this conversation?')) return
    try {
      await api.patch(`/support/conversations/${id}/archive`)
      setSelected(null)
      fetchConversations()
    } catch {}
  }

  const fetchLeads = async (page = 1) => {
    setLeadsLoading(true)
    try {
      const res = await api.get(`/landing-chat/sessions?page=${page}`)
      setLeads(res.data.sessions || [])
      setLeadsTotal(res.data.total || 0)
      setLeadsPage(page)
    } catch {}
    setLeadsLoading(false)
  }

  const fetchLeadDetail = async (id: number) => {
    try {
      const res = await api.get(`/landing-chat/sessions/${id}`)
      setSelectedLeadDetail(res.data)
    } catch {}
  }

  const deleteLead = async (id: number) => {
    if (!window.confirm('Delete this conversation?')) return
    try {
      await api.delete(`/landing-chat/sessions/${id}`)
      setLeads(prev => prev.filter((l: any) => l.id !== id))
      setSelectedLead(null)
      setSelectedLeadDetail(null)
    } catch {}
  }

  useEffect(() => {
    if (activeTab === 'leads') fetchLeads(1)
    if (activeTab === 'inquiries') fetchInquiries(1, inquiryFilter)
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'inquiries') fetchInquiries(1, inquiryFilter)
  }, [inquiryFilter])

  const fetchInquiries = async (page = 1, filter = inquiryFilter) => {
    setInquiriesLoading(true)
    try {
      const statusParam = filter !== 'all' ? `&status=${filter}` : ''
      const [listRes, statsRes, colorStatsRes] = await Promise.all([
        api.get(`/contact?limit=25&page=${page}${statusParam}`),
        api.get('/contact/stats'),
        api.get('/color-reference/visit-stats')
      ])
      setInquiries(listRes.data.inquiries || [])
      setInquiryTotalPages(listRes.data.pages || 1)
      setInquiryPage(page)
      setInquiryStats(statsRes.data)
      setColorVisitStats(colorStatsRes.data)
    } catch {}
    setInquiriesLoading(false)
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col md:flex-row overflow-hidden lg:pl-[72px]">
      {/* Sidebar */}
      <div className={`${selected ? 'hidden md:flex' : 'flex'} md:w-72 w-full flex-shrink-0 border-r border-gray-100 dark:border-[#1e3a5f] flex-col`}>
        <div className="px-4 py-4 border-b border-gray-100 dark:border-[#1e3a5f]">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-display font-bold text-navy-900 dark:text-white text-lg">Support</h1>
            {unread > 0 && activeTab === 'client' && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unread}</span>
            )}
          </div>
          {/* Tab switcher */}
          <div className="flex gap-1 bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1 mb-3">
            <button onClick={() => setActiveTab('client')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-lg font-medium transition-colors ${activeTab === 'client' ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
              <MessageCircle size={12} /> Client Chat
            </button>
            <button onClick={() => setActiveTab('leads')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-lg font-medium transition-colors ${activeTab === 'leads' ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
              <Globe size={12} /> Landing Leads
            </button>
            <button onClick={() => setActiveTab('inquiries')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-lg font-medium transition-colors ${activeTab === 'inquiries' ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
              <FileText size={12} /> File Inquiries
            </button>
          </div>
          {activeTab === 'client' && (
            <button onClick={() => setShowArchived(v => !v)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-gray-100 text-gray-600 border-gray-200' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {showArchived ? <><ArrowLeft size={12} className="inline mr-1" />Active</> : <><FolderArchive size={12} className="inline mr-1" />Archived</>}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'inquiries' ? (
            inquiriesLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading inquiries...</div>
            ) : (
              <>
                {colorVisitStats && (
                <div className="px-4 py-3 border-b border-gray-100 dark:border-[#1e3a5f] bg-gray-50 dark:bg-[#0d1e35]">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Color Reference Guide Usage</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-base font-bold text-navy-800 dark:text-white leading-none">{colorVisitStats.total}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Total visits</p>
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-400 leading-none">{colorVisitStats.last7Days}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Last 7 days</p>
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-400 leading-none">{colorVisitStats.matchedCount}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Used image match</p>
                    </div>
                  </div>
                </div>
                )}
                {inquiryStats && (
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-[#1e3a5f] bg-gray-50 dark:bg-[#0d1e35] space-y-2.5">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-lg font-bold text-navy-800 dark:text-white leading-none">{inquiryStats.total}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Total visits</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-500 leading-none">{inquiryStats.submitted}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Submitted</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-400 leading-none">{inquiryStats.last7Days}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Last 7 days</p>
                      </div>
                    </div>
                    {inquiryStats.avgHealthScore !== null && (
                      <p className="text-[10px] text-gray-400 text-center">Avg health score: <span className="font-semibold text-navy-700 dark:text-gray-300">{inquiryStats.avgHealthScore}/100</span></p>
                    )}
                    {Object.keys(inquiryStats.fileTypeCounts || {}).length > 0 && (
                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                        {Object.entries(inquiryStats.fileTypeCounts).map(([ext, count]: any) => (
                          <span key={ext} className="text-[10px] bg-white dark:bg-[#1a3050] text-navy-600 dark:text-gray-300 px-2 py-0.5 rounded-full font-medium uppercase">
                            {ext}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-1 px-4 py-2 border-b border-gray-100 dark:border-[#1e3a5f]">
                  {(['all', 'submitted', 'abandoned'] as const).map(f => (
                    <button key={f} onClick={() => setInquiryFilter(f)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-medium capitalize transition-colors ${inquiryFilter === f ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#1a3050] text-gray-400'}`}>
                      {f === 'submitted' ? 'Submitted' : f === 'abandoned' ? 'Viewed only' : 'All'}
                    </button>
                  ))}
                </div>
                {inquiries.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText size={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No file check inquiries{inquiryFilter !== 'all' ? ` matching "${inquiryFilter}"` : ''} yet</p>
                  </div>
                ) : (
                  inquiries.map((inq: any) => (
                    <button key={inq.id} onClick={() => setSelectedInquiry(inq)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 dark:border-[#1e3a5f] hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors ${selectedInquiry?.id === inq.id ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-navy-800 dark:text-white truncate">{inq.name === '(not provided)' ? 'Anonymous visitor' : inq.name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${inq.status === 'submitted' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                          {inq.status === 'submitted' ? 'Submitted' : 'Viewed only'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{inq.metadata?.fileName || 'No file name'}</p>
                      <p className="text-[10px] text-gray-300 mt-0.5">{new Date(inq.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </button>
                  ))
                )}
                {inquiryTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-100 dark:border-[#1e3a5f]">
                    <button onClick={() => fetchInquiries(inquiryPage - 1, inquiryFilter)} disabled={inquiryPage === 1}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#1a3050] text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed">
                      Prev
                    </button>
                    <span className="text-[10px] text-gray-400">Page {inquiryPage} of {inquiryTotalPages}</span>
                    <button onClick={() => fetchInquiries(inquiryPage + 1, inquiryFilter)} disabled={inquiryPage === inquiryTotalPages}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#1a3050] text-gray-500 disabled:opacity-40 disabled:cursor-not-allowed">
                      Next
                    </button>
                  </div>
                )}
              </>
            )
          ) : activeTab === 'leads' ? (
            leadsLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading leads...</div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center">
                <Globe size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No landing page leads yet</p>
              </div>
            ) : (
              <>
                {leads.map(lead => (
                  <div key={lead.id}
                    onClick={() => { setSelectedLead(lead); fetchLeadDetail(lead.id) }}
                    className={`px-4 py-3 border-b border-gray-50 dark:border-[#1e3a5f] cursor-pointer transition-colors ${selectedLead?.id === lead.id ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#1a3050]'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-navy-800 dark:text-white truncate">
                        {lead.visitor_name || 'Anonymous'}
                      </p>
                      {lead.escalated && (
                        <span className="text-[10px] bg-orange-50 text-orange-500 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">Lead</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{lead.visitor_email || 'No email'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-gray-400">{lead.message_count} messages</span>
                      <span className="text-[10px] text-gray-300">·</span>
                      <span className="text-[10px] text-gray-400">{new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>
                ))}
                {leadsTotal > 20 && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <button onClick={() => fetchLeads(leadsPage - 1)} disabled={leadsPage === 1}
                      className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30 flex items-center gap-1"><ChevronLeft size={12} /> Prev</button>
                    <span className="text-xs text-gray-400">{leadsPage} / {Math.ceil(leadsTotal / 20)}</span>
                    <button onClick={() => fetchLeads(leadsPage + 1)} disabled={leadsPage >= Math.ceil(leadsTotal / 20)}
                      className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30">Next →</button>
                  </div>
                )}
              </>
            )
          ) : loading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">No conversations</div>
          ) : (
            conversations.map(conv => (
              <div key={conv.id} onClick={() => loadConversation(conv.id)}
                className={`px-4 py-3 border-b border-gray-50 dark:border-[#1e3a5f] cursor-pointer transition-colors ${
                  selected?.id === conv.id ? 'bg-orange-50 dark:bg-orange-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#1a3050]'
                }`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-navy-800 dark:text-white truncate">{conv.clientName}</p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[conv.status] || 'bg-gray-100 text-gray-500'}`}>
                    {conv.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">{conv.companyName}</p>
                {conv.lastMessage && (
                  <p className="text-xs text-gray-400 truncate mt-1">{conv.lastMessage.content}</p>
                )}
                <p className="text-xs text-gray-300 mt-1">
                  {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* File Check Inquiries Detail */}
        {activeTab === 'inquiries' && (
          <div className="flex-1 flex items-center justify-center">
            {!selectedInquiry ? (
              <div className="text-center">
                <FileText size={48} className="text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Select an inquiry to view details</p>
              </div>
            ) : (
              <div className="w-full max-w-lg px-6">
                <div className="bg-white dark:bg-[#0f2137] rounded-2xl border border-gray-100 dark:border-[#1e3a5f] p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold text-navy-900 dark:text-white text-lg">
                      {selectedInquiry.name === '(not provided)' ? 'Anonymous visitor' : selectedInquiry.name}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${selectedInquiry.status === 'submitted' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {selectedInquiry.status === 'submitted' ? 'Submitted contact info' : 'Viewed results only'}
                    </span>
                  </div>

                  {selectedInquiry.email !== '(not provided)' && (
                    <div>
                      <p className="text-xs text-gray-400">Email</p>
                      <a href={`mailto:${selectedInquiry.email}`} className="text-sm text-orange-500 hover:text-orange-600 font-medium">{selectedInquiry.email}</a>
                    </div>
                  )}
                  {selectedInquiry.companyName && (
                    <div>
                      <p className="text-xs text-gray-400">Company</p>
                      <p className="text-sm text-navy-800 dark:text-white">{selectedInquiry.companyName}</p>
                    </div>
                  )}
                  {selectedInquiry.message && (
                    <div>
                      <p className="text-xs text-gray-400">Message</p>
                      <p className="text-sm text-navy-800 dark:text-white">{selectedInquiry.message}</p>
                    </div>
                  )}
                  {selectedInquiry.metadata?.fileName && (
                    <div>
                      <p className="text-xs text-gray-400">File checked</p>
                      <p className="text-sm text-navy-800 dark:text-white">{selectedInquiry.metadata.fileName} ({selectedInquiry.metadata.fileType})</p>
                    </div>
                  )}
                  {typeof selectedInquiry.metadata?.healthScore === 'number' && (
                    <div>
                      <p className="text-xs text-gray-400">Health score</p>
                      <p className="text-sm font-bold text-navy-800 dark:text-white">{selectedInquiry.metadata.healthScore} / 100</p>
                    </div>
                  )}
                  {Array.isArray(selectedInquiry.metadata?.failedChecks) && selectedInquiry.metadata.failedChecks.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400">Issues flagged</p>
                      <p className="text-sm text-navy-800 dark:text-white">{selectedInquiry.metadata.failedChecks.join(', ')}</p>
                    </div>
                  )}
                  <div className="pt-2 border-t border-gray-100 dark:border-[#1e3a5f]">
                    <p className="text-xs text-gray-400">Submitted {new Date(selectedInquiry.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Landing Leads Detail */}
        {activeTab === 'leads' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {!selectedLead ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Globe size={48} className="text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Select a lead to view conversation</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f]">
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setSelectedLead(null); setSelectedLeadDetail(null) }}
                      className="md:hidden text-gray-400 hover:text-gray-600"><ArrowLeft size={16} /></button>
                    <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy-800 dark:text-white text-sm">{selectedLead.visitor_name || 'Anonymous Visitor'}</p>
                      <button
                        onClick={() => {
                          if (!selectedLead.visitor_email) return
                          navigator.clipboard.writeText(selectedLead.visitor_email)
                          setCopiedEmail(true)
                          setTimeout(() => setCopiedEmail(false), 1500)
                        }}
                        className="text-xs text-gray-400 flex items-center gap-1 hover:text-orange-500 transition-colors group">
                        <Mail size={10} />
                        <span>{selectedLead.visitor_email || 'No email captured'}</span>
                        {selectedLead.visitor_email && (
                          copiedEmail
                            ? <Check size={10} className="text-green-500" />
                            : <Copy size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {selectedLead.escalated && (
                        <span className="text-xs bg-orange-50 text-orange-500 px-2 py-1 rounded-full font-medium">Requested Contact</span>
                      )}
                      <span className="text-xs text-gray-400">{selectedLead.message_count} messages</span>
                      <button onClick={() => deleteLead(selectedLead.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                        title="Delete conversation">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {selectedLeadDetail ? (
                    (selectedLeadDetail.messages as any[])?.map((m: any, i: number) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                          m.role === 'user'
                            ? 'bg-orange-500 text-white rounded-br-sm'
                            : 'bg-gray-100 dark:bg-[#1a3050] text-navy-800 dark:text-gray-200 rounded-bl-sm'
                        }`}>
                          {m.role !== 'user' && <p className="text-[10px] font-semibold text-orange-500 mb-1">Ink</p>}
                          <p>{m.content}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-400 text-sm py-8">Loading conversation...</div>
                  )}
                </div>
                <div className="px-4 py-3 mb-16 sm:mb-0 border-t border-gray-100 dark:border-[#1e3a5f] bg-gray-50 dark:bg-[#0d1e35]">
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Started {new Date(selectedLead.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    {selectedLead.visitor_email && (
                      <a href={`mailto:${selectedLead.visitor_email}`}
                        className="flex items-center gap-1 text-orange-500 hover:text-orange-600 font-medium transition-colors">
                        <Mail size={11} /> Reply via email
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Client Support Panel */}
        {activeTab === 'client' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {selected ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelected(null)} className="md:hidden text-gray-400 hover:text-gray-600"><ArrowLeft size={16} /></button>
                    <div>
                      <p className="font-semibold text-navy-900 dark:text-white">{selected.client.name}</p>
                      <p className="text-xs text-gray-400">{selected.client.company} · {selected.client.email}</p>
                      {selected.client.subscription && (
                        <p className="text-xs text-orange-500 mt-0.5">
                          {selected.client.subscription.jobsUsed}/{selected.client.subscription.totalJobsAllowed} jobs used · Expires {new Date(selected.client.subscription.endDate).toLocaleDateString('en-IN')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[selected.status] || 'bg-gray-100 text-gray-500'}`}>
                      {selected.status === 'staff_active' ? <span className="flex items-center gap-1"><UserRound size={11} /> Staff Active</span> : selected.status}
                    </span>
                    {selected.status !== 'archived' && (
                      <>
                        {selected.status === 'staff_active' ? (
                          <button onClick={async () => {
                            await api.patch(`/support/conversations/${selected.id}/handback`)
                            setSelected((prev: any) => prev ? { ...prev, status: 'active' } : null)
                            fetchConversations()
                          }} className="flex items-center gap-1 text-xs text-green-500 hover:text-green-600 px-2 py-1.5 rounded-lg hover:bg-green-50 transition-colors">
                            <Bot size={13} /> Hand Back to AI
                          </button>
                        ) : (
                          <button onClick={async () => {
                            await api.patch(`/support/conversations/${selected.id}/takeover`)
                            setSelected((prev: any) => prev ? { ...prev, status: 'staff_active' } : null)
                            fetchConversations()
                          }} className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                            <UserRound size={13} /> Take Over
                          </button>
                        )}
                        <button onClick={() => handleArchive(selected.id)}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                          <Archive size={13} /> Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {selected.messages.map((m: any) => (
                    <div key={m.id} className={`flex ${m.senderType === 'client' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                        m.senderType === 'client'
                          ? 'bg-gray-100 dark:bg-[#1a3050] text-navy-800 dark:text-gray-200 rounded-bl-sm'
                          : m.senderType === 'ai'
                          ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-200 rounded-br-sm border border-orange-100 dark:border-orange-700/30'
                          : 'bg-blue-500 text-white rounded-br-sm'
                      }`}>
                        {m.senderType === 'ai' && <p className="text-xs font-semibold text-orange-400 mb-1 flex items-center gap-1"><Sparkles size={10} /> Ink (AI)</p>}
                        {m.senderType === 'staff' && <p className="text-xs font-semibold text-blue-200 mb-1">{m.senderName || 'Support'}</p>}
                        {m.senderType === 'client' && <p className="text-xs font-semibold text-gray-400 mb-1">Client</p>}
                        <p>{m.content}</p>
                        <p className="text-xs opacity-60 mt-1">{new Date(m.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                {/* Reply */}
                {selected.status !== 'archived' && (
                  <div className="px-4 py-3 mb-16 sm:mb-0 border-t border-gray-100 dark:border-[#1e3a5f] flex items-end gap-2">
                    <div className="flex-1 space-y-2">
                      {improvedReply && (
                        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-700/30 rounded-xl p-3 space-y-2">
                          <p className="text-xs font-semibold text-purple-500 flex items-center gap-1"><Sparkles size={10} /> Improvised</p>
                          <p className="text-sm text-purple-900 dark:text-purple-200">{improvedReply}</p>
                          <div className="flex gap-2">
                            <button onClick={() => { setReply(improvedReply); setImprovedReply('') }}
                              className="text-xs bg-purple-500 text-white px-3 py-1 rounded-lg">Use this</button>
                            <button onClick={() => setImprovedReply('')} className="text-xs text-gray-400">Dismiss</button>
                          </div>
                        </div>
                      )}
                      <textarea rows={2} className="w-full border border-gray-200 dark:border-[#1e3a5f] rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-orange-400 dark:bg-[#1a3050] dark:text-white"
                        placeholder="Type your reply..."
                        value={reply} onChange={e => { setReply(e.target.value); setImprovedReply('') }}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply() } }} />
                    </div>
                    <div className="flex flex-col gap-2">
                      {reply.trim().length > 5 && !improvedReply && (
                        <button onClick={async () => {
                          setImprovingReply(true)
                          try {
                            const res = await api.post('/jobs/improve-note', { text: reply })
                            setImprovedReply(res.data.improved)
                          } catch {}
                          setImprovingReply(false)
                        }} disabled={improvingReply}
                          className="w-10 h-10 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center text-xs font-bold transition-colors"
                          title="Improvise">
                          {improvingReply ? <Sparkles size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        </button>
                      )}
                      <button onClick={handleReply} disabled={!reply.trim() || sending}
                        className="w-10 h-10 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0 mr-14 sm:mr-0">
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle size={40} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-400">Select a conversation to view</p>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}